// src/components/patient/notifications/AppointmentAttention.jsx
//
// Dashboard attention panel (Patient Appointments Task 4): next appointment,
// payment-required and forms-required items, and the unread bell count -- all
// from GET /patients/me/dashboard/appointment-attention, which projects the
// same workspace service the appointment list uses. Links are internal only.

import React, { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";

import { readApiError } from "../../../lib/apiErrors";
import { browserTimezone, formatDateTimeInZone } from "../../../lib/displayTimezone";
import { isSafeNotificationRoute } from "../../../lib/notificationLinks";
import useSequencedRequest from "../../../lib/useSequencedRequest";
import { patientNotificationsApi } from "../../../services/patientNotifications";

function SafeLink({ to, children, className }) {
  return isSafeNotificationRoute(to) ? <Link to={to} className={className}>{children}</Link> : <span className={className}>{children}</span>;
}

export default function AppointmentAttention() {
  const zone = browserTimezone() || "UTC";
  const request = useSequencedRequest(null);
  const run = request.run;
  const load = useCallback(() => run(({ signal }) => patientNotificationsApi.attention(zone, { signal })), [run, zone]);

  useEffect(() => {
    load();
  }, [load]);

  const data = request.data;
  if (request.loading && !data) return <section className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 text-sm text-slate-500" aria-busy="true">Loading your appointment updates…</section>;
  if (request.error && !data) {
    return (
      <section role="alert" className="mb-6 flex items-center justify-between rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <span>{readApiError(request.error, "Appointment updates could not be loaded.")}</span>
        <button type="button" onClick={load} className="rounded-xl border border-rose-300 bg-white px-3 py-1 text-xs font-semibold">Retry</button>
      </section>
    );
  }
  if (!data) return null;
  const next = data.next_appointment;
  return (
    <section className="mb-6 grid gap-3 md:grid-cols-3" aria-label="Appointment updates" data-testid="appointment-attention">
      <div className="rounded-3xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next appointment</p>
        {next ? (
          <SafeLink to={next.link} className="mt-2 block">
            <span className="block text-sm font-semibold">{next.service_name}</span>
            <span className="block text-xs text-slate-600">{next.doctor_name}{next.clinic_name ? ` · ${next.clinic_name}` : ""}</span>
            <span className="block text-xs text-slate-600">{formatDateTimeInZone(next.start_at, zone)} ({zone})</span>
            <span className="block text-xs font-semibold text-slate-700">{next.status_label}</span>
          </SafeLink>
        ) : (
          <p className="mt-2 text-sm text-slate-600">No upcoming appointments. <Link to="/patient/book-appointment" className="font-semibold text-sky-700">Book one</Link></p>
        )}
      </div>
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Needs attention</p>
        {data.payment_required.length || data.forms_required.length ? (
          <ul className="mt-2 space-y-1 text-sm">
            {data.payment_required.map((item) => (
              <li key={`pay-${item.id}`}><SafeLink to={item.link} className="font-semibold text-amber-900">Payment required · {item.service_name}</SafeLink></li>
            ))}
            {data.forms_required.map((item) => (
              <li key={`forms-${item.id}`}><SafeLink to={item.link} className="font-semibold text-amber-900">{item.pending_required_forms_count} form{item.pending_required_forms_count === 1 ? "" : "s"} to complete · {item.service_name}</SafeLink></li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-amber-900">Nothing needs your attention.</p>
        )}
      </div>
      <div className="rounded-3xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notifications</p>
        <p className="mt-2 text-2xl font-semibold" data-testid="attention-unread">{data.unread_notification_count}</p>
        <Link to="/patient/notifications" className="text-sm font-semibold text-sky-700">View notifications</Link>
      </div>
    </section>
  );
}
